/**
 * React Query hooks for User Profile and Organization operations
 * Provides data fetching, caching, and mutations for user-related operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiBaseUrl, createAuthHeaders } from '@/utils/apiUtils';
import { fetchWithAuthRetry } from '@/utils/authErrorHandler';
import { toast } from 'sonner';
import { auth } from '@/config/firebase';
import { devLog, devWarn, devError } from '@/utils/devLog';
import type { UserRole } from '@/types/userRole';

// Types
interface UserProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: UserRole;
  status: string;
  subscription: string;
  organizationId: string;
  organizationName: string;
  position?: string;
  location?: string;
  bio?: string;
  website?: string;
  linkedin?: string;
  twitter?: string;
  investmentFocus?: string;
  displayName?: string;
  projectType?: string;
  projectDescription?: string;
}

/**
 * Whitelisted fields the current user may edit on their own profile.
 * Role, status, orgId, email, and subscription are NOT included — those
 * are admin-only or managed via dedicated flows (change-email, subscription portal).
 */
interface MyProfileUpdatePayload {
  firstName?: string;
  lastName?: string;
  position?: string;
  investmentFocus?: string;
  phone?: string;
  location?: string;
  bio?: string;
  website?: string;
  linkedin?: string;
  twitter?: string;
  displayName?: string;
  projectType?: string;
  projectDescription?: string;
}

interface NotificationMetadata {
  projectId?: string;
  userId?: string;
  action?: string;
  [key: string]: unknown;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: string;
  metadata?: NotificationMetadata;
}

interface ProfileRegistrationData {
  firstName: string;
  lastName: string;
  role: UserRole;
  providerId?: string;
  // Optional fields for all users
  phone?: string;
  location?: string;
  bio?: string;
  website?: string;
  linkedin?: string;
  twitter?: string;
  emailNotifications?: boolean;
  projectUpdates?: boolean;
  organizationName?: string;
  position?: string;
  investmentFocus?: string;
}

// Query keys for consistent cache management
const USER_QUERY_KEYS = {
  profile: ['user', 'profile'] as const,
  notifications: ['user', 'notifications'] as const,
};

/**
 * Hook to fetch current user's profile
 */
export function useUserProfile() {
  return useQuery({
    queryKey: USER_QUERY_KEYS.profile,
    queryFn: async () => {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      // Initialize with basic info from Firebase Auth
      const basicUserData = {
        email: user.email || '',
        firstName: user.displayName?.split(' ')[0] || '',
        lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
      };

      try {
        const headers = await createAuthHeaders();
        const response = await fetchWithAuthRetry(
          `${getApiBaseUrl()}/api/auth/user-profile`,
          { headers },
          'User: Fetch User Profile'
        );
        const data = await response.json();

        return {
          ...basicUserData,
          ...data.user,
        } as UserProfileData;
      } catch {
        devWarn('Failed to fetch user profile from server. Using basic info from Firebase Auth.');
        return basicUserData as UserProfileData;
      }
    },
    enabled: !!auth.currentUser,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook for the current user to self-edit their own profile.
 *
 * Targets `PUT /api/users/me/profile` (Wave B1 endpoint).
 * Only whitelisted fields are sent; role/status/orgId/email/subscription
 * are intentionally excluded from the payload.
 *
 * Uses optimistic updates with rollback identical to useUpdateUserProfile so
 * the UI feels instant. On success the cache is synced with the server response.
 *
 * staleTime for the user-profile query is 5 minutes, aligned with the global
 * QueryClient default and the Wave 2G org-members alignment.
 */
export function useUpdateMyProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: MyProfileUpdatePayload) => {
      const headers = await createAuthHeaders();
      const response = await fetchWithAuthRetry(
        `${getApiBaseUrl()}/api/auth/users/me/profile`,
        {
          method: 'PUT',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
        'User: Update My Profile (self-edit)'
      );

      if (!response.ok) {
        // Surface structured validation errors from the BE if present
        let errorMsg = 'Failed to update profile';
        try {
          const errBody = await response.json();
          errorMsg = errBody?.error || errBody?.message || errorMsg;
        } catch {
          // response body not JSON — keep default message
        }
        throw new Error(errorMsg);
      }

      return response.json();
    },

    onMutate: async (payload) => {
      // Cancel any in-flight refetches to avoid overwriting the optimistic update
      await queryClient.cancelQueries({ queryKey: USER_QUERY_KEYS.profile });

      // Snapshot for rollback
      const previousProfile = queryClient.getQueryData<UserProfileData>(USER_QUERY_KEYS.profile);

      // Optimistic update
      queryClient.setQueryData<UserProfileData>(USER_QUERY_KEYS.profile, (old) => {
        if (!old) return old;
        devLog('[UserCache] Optimistic self-edit update:', payload);
        return { ...old, ...payload };
      });

      return { previousProfile };
    },

    onError: (error: Error, _variables, context) => {
      devError('Error updating own profile:', error);

      // Roll back to previous state
      if (context?.previousProfile) {
        devLog('[UserCache] Rolling back self-edit update');
        queryClient.setQueryData(USER_QUERY_KEYS.profile, context.previousProfile);
      }

      // Safety-net invalidation
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.profile });

      toast.error(error.message || 'Failed to update profile');
    },

    onSuccess: (data) => {
      // Sync cache with authoritative server response
      if (data?.user) {
        queryClient.setQueryData(USER_QUERY_KEYS.profile, (old: UserProfileData | undefined) => ({
          ...old,
          ...data.user,
        }));
      } else {
        // No user object in response — invalidate to force fresh fetch
        queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.profile });
      }
      toast.success('Profile updated successfully');
    },
  });
}

/**
 * Hook to fetch user's notifications
 */
export function useUserNotifications() {
  return useQuery({
    queryKey: USER_QUERY_KEYS.notifications,
    queryFn: async () => {
      const headers = await createAuthHeaders();
      const response = await fetchWithAuthRetry(
        `${getApiBaseUrl()}/api/notifications`,
        { headers },
        'User: Fetch Notifications'
      );

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      return (data.notifications || []) as Notification[];
    },
    enabled: !!auth.currentUser,
    staleTime: 1000 * 30, // 30 seconds - refresh more frequently
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to mark notification as read
 * Uses optimistic updates with rollback for instant UI feedback
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const headers = await createAuthHeaders();
      const response = await fetchWithAuthRetry(
        `${getApiBaseUrl()}/api/notifications/${notificationId}/read`,
        {
          method: 'PUT',
          headers: {
            ...headers,
            'Content-Type': 'application/json'
          }
        },
        'User: Mark Notification Read'
      );

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      return response.json();
    },
    onMutate: async (notificationId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: USER_QUERY_KEYS.notifications });

      // Snapshot for rollback
      const previousNotifications = queryClient.getQueryData<Notification[]>(USER_QUERY_KEYS.notifications);

      // Optimistic update
      queryClient.setQueryData<Notification[]>(USER_QUERY_KEYS.notifications, (old) => {
        if (!old) return old;
        devLog('[UserCache] Optimistic mark as read:', notificationId);
        return old.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        );
      });

      return { previousNotifications };
    },
    onError: (error: Error, _notificationId, context) => {
      devError('Error marking notification as read:', error);

      // Rollback on error
      if (context?.previousNotifications) {
        devLog('[UserCache] Rolling back notification read status');
        queryClient.setQueryData(USER_QUERY_KEYS.notifications, context.previousNotifications);
      }

      // Safety net invalidation
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.notifications });
    },
    // No onSuccess needed - optimistic update is sufficient
  });
}

/**
 * Hook to delete a notification
 * Uses optimistic updates with rollback for instant UI feedback
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const headers = await createAuthHeaders();
      const response = await fetchWithAuthRetry(
        `${getApiBaseUrl()}/api/notifications/${notificationId}`,
        {
          method: 'DELETE',
          headers
        },
        'User: Delete Notification'
      );

      if (!response.ok) {
        throw new Error('Failed to delete notification');
      }

      return notificationId;
    },
    onMutate: async (notificationId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: USER_QUERY_KEYS.notifications });

      // Snapshot for rollback
      const previousNotifications = queryClient.getQueryData<Notification[]>(USER_QUERY_KEYS.notifications);

      // Optimistic update - remove notification immediately
      queryClient.setQueryData<Notification[]>(USER_QUERY_KEYS.notifications, (old) => {
        if (!old) return old;
        devLog('[UserCache] Optimistic delete notification:', notificationId);
        return old.filter(n => n.id !== notificationId);
      });

      return { previousNotifications };
    },
    onError: (error: Error, _notificationId, context) => {
      devError('Error deleting notification:', error);

      // Rollback on error
      if (context?.previousNotifications) {
        devLog('[UserCache] Rolling back notification deletion');
        queryClient.setQueryData(USER_QUERY_KEYS.notifications, context.previousNotifications);
      }

      // Safety net invalidation
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEYS.notifications });

      toast.error('Failed to delete notification');
    },
    onSuccess: () => {
      toast.success('Notification deleted');
    },
  });
}

// ---------------------------------------------------------------------------
// Subscription upgrade request
// ---------------------------------------------------------------------------

interface SubscriptionUpgradePayload {
  /** The plan the user wants to move to (e.g. "pro" | "enterprise"). */
  desiredSubscription: string;
  /** Optional free-text note from the user. */
  message?: string;
  /** Optional — current plan; BE also reads it from Firestore if omitted. */
  currentSubscription?: string;
}

/**
 * Hook to request a subscription upgrade.
 *
 * POSTs to `POST /api/auth/request-subscription-upgrade`.
 * The BE validates with `subscriptionUpgradeSchema` (desiredSubscription required,
 * currentSubscription + message optional) and sends an email to admin.
 *
 * Success / error toasts are raised here so callers don't need to.
 */
export function useRequestSubscriptionUpgrade() {
  return useMutation({
    mutationFn: async (payload: SubscriptionUpgradePayload) => {
      const headers = await createAuthHeaders();
      const response = await fetchWithAuthRetry(
        `${getApiBaseUrl()}/api/auth/request-subscription-upgrade`,
        {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
        'User: Request Subscription Upgrade'
      );

      if (!response.ok) {
        let errorMsg = 'Failed to send upgrade request';
        try {
          const body = await response.json();
          errorMsg = body?.message ?? body?.error ?? errorMsg;
        } catch {
          // response body not JSON — keep default
        }
        throw new Error(errorMsg);
      }

      return response.json();
    },

    onSuccess: () => {
      toast.success(
        'Upgrade request sent. Our team will follow up within 1-2 business days.'
      );
    },

    onError: (error: Error) => {
      devError('Error requesting subscription upgrade:', error);
      toast.error(error.message ?? 'Failed to send upgrade request');
    },
  });
}

/**
 * Hook to complete user profile registration
 */
export function useCompleteProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileData: ProfileRegistrationData) => {
      const headers = await createAuthHeaders();
      const response = await fetchWithAuthRetry(
        `${getApiBaseUrl()}/api/auth/register-profile`,
        {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(profileData)
        },
        'User: Complete Profile Registration'
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Failed to complete profile');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate user queries to refresh profile data
      queryClient.invalidateQueries({ queryKey: ['user'] });
      toast.success('Profile completed successfully!');
    },
    onError: (error: Error) => {
      devError('Error completing profile:', error);
      toast.error(error.message || 'Failed to complete profile');
    },
  });
}
